import { cloneElement, type ReactElement, type ReactNode } from 'react'
import { Focusable } from 'react-aria-components'
import { Link } from 'react-router-dom'

import Tooltip from '@components/designSystem/Tooltip'
import { useDesktopNavigation } from '@components/navbar/desktop/DesktopNavigationContext'
import { cx } from '@utils/cx'

const transitionDuration = 'duration-200'

interface NavItemProps {
  className?: string
  to: string
  label: ReactNode
  isActive: boolean
  icon: ReactElement & { props: { className?: string } }
  meta?: ReactElement
}

function NavItem({ className, to, label, isActive, icon, meta }: NavItemProps) {
  const { isExpanded } = useDesktopNavigation()
  const linkClasses = cx(
    `
      flex min-h-11 w-full flex-row items-center gap-x-3 overflow-hidden rounded-md px-3 text-sm font-medium
      transition-colors
    `,
    'hover:bg-bg-tertiary hover:text-text-primary', // Hover and focus styles
    'focus:text-text-primary focus:ring-1 focus:ring-bg-primary', // Focus styles
    isActive ? 'bg-bg-tertiary text-text-primary' : 'text-text-secondary', // Active/inactive styles
    transitionDuration,
    className
  )

  return (
    <Tooltip title={label} placement='right' isDisabled={isExpanded}>
      <Focusable>
        <Link to={to} className={linkClasses}>
          {/* By cloning the icon, we can safely add the flex-shrink-0 class without breaking the original component. */}
          {cloneElement(icon, { className: cx(icon.props.className, 'shrink-0') })}
          <span className='whitespace-nowrap'>{label}</span>
          {isExpanded && meta}
        </Link>
      </Focusable>
    </Tooltip>
  )
}

export default NavItem

